import { Vec3 } from '@/core/math/Vec3';
import {
  OrbitalElements,
  apoapsisRadius,
  elementsFromState,
  isHyperbolic,
  orbitalPeriod,
  periapsisRadius,
  stateVectorAt,
  timeToApoapsis,
  timeToPeriapsis,
  timeToTrueAnomaly,
} from '@/universe/OrbitalElements';
import { CelestialBody } from '@/universe/CelestialBody';
import { SolarSystem } from '@/universe/SolarSystem';

export interface TrajectoryEvent {
  kind: 'impact' | 'atmosphereEntry' | 'soiExit' | 'soiEntry' | 'periapsis' | 'apoapsis' | 'closestApproach';
  /** Simulation time of the event, seconds. */
  time: number;
  /** Position in the reference body's inertial frame. */
  position: Vec3;
  /** Body involved, for SOI and approach events. */
  body?: CelestialBody;
  /** Altitude above the datum, or separation distance for approaches. */
  distance?: number;
  label: string;
}

export interface TrajectoryPrediction {
  referenceBody: CelestialBody;
  elements: OrbitalElements;
  /** Sampled path in the reference body's inertial frame. */
  points: Vec3[];
  /** Times matching `points`. */
  times: number[];
  periapsisAltitude: number;
  apoapsisAltitude: number;
  /** Infinity for escape trajectories. */
  period: number;
  timeToPeriapsis: number;
  timeToApoapsis: number;
  escape: boolean;
  events: TrajectoryEvent[];
  /** True when the path terminates by hitting the body. */
  impacts: boolean;
  /** How far ahead the prediction actually ran, seconds. */
  horizon: number;
}

export interface PredictOptions {
  /** Number of samples along the path. */
  samples?: number;
  /** Maximum look-ahead, seconds. */
  maxHorizon?: number;
  /** Highest terrain on the body, metres. Used for the impact test. */
  terrainCeiling?: number;
  /** Include sphere-of-influence transition detection for child bodies. */
  detectSoiTransitions?: boolean;
}

/**
 * Analytical trajectory prediction.
 *
 * The vessel's current state is converted to Keplerian elements about the
 * reference body and then evaluated forward in closed form. This is the same
 * machinery the analytical time warp uses, which is deliberate: what the
 * manoeuvre display shows and what warping actually does are the same
 * computation, so the prediction cannot silently disagree with the result.
 *
 * The prediction ignores drag and third-body perturbation. That is stated
 * plainly in the UI, and the events it does detect (atmospheric interface,
 * impact, SOI change) are exactly the points at which the approximation stops
 * being valid, so the player is warned before the divergence matters.
 */
export class TrajectoryPredictor {
  constructor(readonly system: SolarSystem) {}

  predict(
    referenceBody: CelestialBody,
    position: Vec3,
    velocity: Vec3,
    t0: number,
    options: PredictOptions = {},
  ): TrajectoryPrediction {
    const samples = options.samples ?? 256;
    const terrainCeiling = options.terrainCeiling ?? 0;
    const detectSoi = options.detectSoiTransitions ?? true;

    const elements = elementsFromState(position, velocity, referenceBody.mu, t0);
    const escape = isHyperbolic(elements);
    const period = orbitalPeriod(elements);

    // A bound orbit is never worth sampling past one period — it repeats. An
    // escape trajectory has no period to stop at, so it gets a default cap of
    // six hours, which an explicit `maxHorizon` is allowed to raise: a patched
    // conic chain has to follow an ejection hyperbola all the way out to the
    // sphere-of-influence boundary, and that is routinely more than six hours.
    const horizon = escape
      ? (options.maxHorizon ?? 6 * 3600)
      : Math.min(options.maxHorizon ?? period, period);

    const points: Vec3[] = [];
    const times: number[] = [];
    const events: TrajectoryEvent[] = [];

    const impactRadius = referenceBody.radius + terrainCeiling;
    const atmosphereRadius = referenceBody.spaceAltitudeRadius;
    const soi = referenceBody.sphereOfInfluence;

    let impacts = false;
    let insideAtmosphere = position.length() < atmosphereRadius;
    let actualHorizon = horizon;

    // Track the closest approach to each child body, and the first SOI entry.
    const children = detectSoi ? referenceBody.children : [];
    const closest = new Map<string, { distance: number; time: number; position: Vec3 }>();
    const soiEntered = new Set<string>();

    for (let i = 0; i <= samples; i++) {
      const dt = (horizon * i) / samples;
      const t = t0 + dt;
      const sv = stateVectorAt(elements, t);
      const r = sv.position.length();

      points.push(sv.position);
      times.push(t);

      // Impact.
      if (r <= impactRadius) {
        impacts = true;
        events.push({
          kind: 'impact',
          time: t,
          position: sv.position,
          distance: r - referenceBody.radius,
          label: `Impact with ${referenceBody.name}`,
        });
        actualHorizon = dt;
        break;
      }

      // Atmospheric interface.
      if (!insideAtmosphere && r <= atmosphereRadius && referenceBody.hasAtmosphere) {
        insideAtmosphere = true;
        events.push({
          kind: 'atmosphereEntry',
          time: t,
          position: sv.position,
          distance: r - referenceBody.radius,
          label: `Atmospheric interface at ${referenceBody.name}`,
        });
      } else if (insideAtmosphere && r > atmosphereRadius) {
        insideAtmosphere = false;
      }

      // Leaving the reference body's sphere of influence.
      if (Number.isFinite(soi) && r > soi) {
        events.push({
          kind: 'soiExit',
          time: t,
          position: sv.position,
          body: referenceBody.parent ?? undefined,
          distance: r,
          label: `Leaving ${referenceBody.name} sphere of influence`,
        });
        actualHorizon = dt;
        break;
      }

      // Entering a moon's sphere of influence, and closest approaches.
      for (const child of children) {
        const childPos = this.system.frames.transformPosition(
          new Vec3(0, 0, 0),
          child.inertialFrameId,
          referenceBody.inertialFrameId,
          t,
        );
        const separation = sv.position.distanceTo(childPos);
        const prev = closest.get(child.id);
        if (!prev || separation < prev.distance) {
          closest.set(child.id, { distance: separation, time: t, position: sv.position.clone() });
        }
        if (!soiEntered.has(child.id) && separation < child.sphereOfInfluence) {
          soiEntered.add(child.id);
          events.push({
            kind: 'soiEntry',
            time: t,
            position: sv.position,
            body: child,
            distance: separation,
            label: `Entering ${child.name} sphere of influence`,
          });
        }
      }
    }

    // Periapsis and apoapsis markers.
    const tPeri = timeToPeriapsis(elements, t0);
    if (Number.isFinite(tPeri) && tPeri <= actualHorizon) {
      events.push({
        kind: 'periapsis',
        time: t0 + tPeri,
        position: stateVectorAt(elements, t0 + tPeri).position,
        distance: periapsisRadius(elements) - referenceBody.radius,
        label: 'Periapsis',
      });
    }
    if (!escape) {
      const tApo = timeToApoapsis(elements, t0);
      if (Number.isFinite(tApo) && tApo <= actualHorizon) {
        events.push({
          kind: 'apoapsis',
          time: t0 + tApo,
          position: stateVectorAt(elements, t0 + tApo).position,
          distance: apoapsisRadius(elements) - referenceBody.radius,
          label: 'Apoapsis',
        });
      }
    }

    for (const [id, info] of closest) {
      if (soiEntered.has(id)) continue;
      const child = this.system.get(id);
      if (info.distance > child.sphereOfInfluence * 12) continue;
      events.push({
        kind: 'closestApproach',
        time: info.time,
        position: info.position,
        body: child,
        distance: info.distance,
        label: `Closest approach to ${child.name}`,
      });
    }

    events.sort((a, b) => a.time - b.time);

    return {
      referenceBody,
      elements,
      points,
      times,
      periapsisAltitude: periapsisRadius(elements) - referenceBody.radius,
      apoapsisAltitude: escape ? Infinity : apoapsisRadius(elements) - referenceBody.radius,
      period,
      timeToPeriapsis: tPeri,
      timeToApoapsis: escape ? Infinity : timeToApoapsis(elements, t0),
      escape,
      events,
      impacts,
      horizon: actualHorizon,
    };
  }

  /**
   * Predicts the trajectory that results from applying an instantaneous
   * velocity change at a future time. This is what the manoeuvre node display
   * draws.
   */
  predictAfterBurn(
    referenceBody: CelestialBody,
    position: Vec3,
    velocity: Vec3,
    t0: number,
    burnTime: number,
    deltaV: Vec3,
    options: PredictOptions = {},
  ): TrajectoryPrediction {
    const elements = elementsFromState(position, velocity, referenceBody.mu, t0);
    const atBurn = stateVectorAt(elements, burnTime);
    const newVelocity = Vec3.addVectors(atBurn.velocity, deltaV);
    return this.predict(referenceBody, atBurn.position, newVelocity, burnTime, options);
  }

  // ==========================================================================
  // Patched-conic chaining
  // ==========================================================================

  /**
   * Propagates a route across sphere-of-influence boundaries.
   *
   * `predict` is deliberately single-conic: it stops the moment its own
   * two-body model stops being the right one. Chaining is the other half of
   * that contract — at each of those stopping points the state is transformed
   * into the frame that *is* now dominant and propagation continues, giving
   * the classic patched-conic route.
   *
   * The important property for a route planner is that every arc is checked
   * against its own reference body: an arc that dips inside a planet reports an
   * `impact` on that planet, which is what makes "does this route fly through
   * something" answerable at all. A straight-line steering rule cannot answer
   * it, and a single conic about the departure body cannot either, because the
   * intervening planet is simply not in that conic's model.
   *
   * Encounter detection lives here rather than in `predict` because it needs
   * sub-sample refinement: a coarse sample walk can step clean over a small
   * moon's sphere of influence, so each candidate body's separation curve is
   * scanned for a bracketed minimum and refined before the crossing time is
   * bisected out of it.
   */
  predictChained(
    referenceBody: CelestialBody,
    position: Vec3,
    velocity: Vec3,
    t0: number,
    options: ChainOptions = {},
  ): ChainedPrediction {
    const maxArcs = options.maxArcs ?? 6;
    const totalHorizon = options.totalHorizon ?? 30 * 86400;
    const ceilingFor = options.terrainCeilingFor ?? (() => options.terrainCeiling ?? 0);
    const target = options.arriveAtBodyId ? (this.system.find(options.arriveAtBodyId) ?? null) : null;

    const arcs: RouteArc[] = [];
    const events: TrajectoryEvent[] = [];

    let body = referenceBody;
    let pos = position.clone();
    let vel = velocity.clone();
    let t = t0;

    let impacts = false;
    let impactBody: CelestialBody | null = null;
    let arrived = false;
    let arrivalTime = Infinity;
    let targetMiss = Infinity;
    let targetMissTime = t0;
    let targetPeriapsis = Infinity;

    for (let arcIndex = 0; arcIndex < maxArcs; arcIndex++) {
      const remaining = totalHorizon - (t - t0);
      if (remaining <= 0) break;

      const prediction = this.predict(body, pos, vel, t, {
        samples: options.samples ?? 96,
        maxHorizon: Math.min(remaining, this.arcHorizon(elementsFromState(pos, vel, body.mu, t), body, t)),
        terrainCeiling: ceilingFor(body),
        // Chained encounter detection is done below, with refinement `predict`'s
        // per-sample scan does not do. Turning its own scan off also removes the
        // per-sample-per-child frame transform, which is most of `predict`'s cost.
        detectSoiTransitions: false,
      });

      const arcImpact = prediction.events.find((e) => e.kind === 'impact') ?? null;
      const arcSoiExit = prediction.events.find((e) => e.kind === 'soiExit') ?? null;
      const arcEndTime = prediction.times[prediction.times.length - 1] ?? t;

      // Track the closest the ship comes to the target anywhere on this arc.
      if (target && target.id !== body.id) {
        const near = this.closestSeparation(prediction, body, target);
        if (near && near.distance < targetMiss) {
          targetMiss = near.distance;
          targetMissTime = near.time;
        }
      } else if (target && target.id === body.id) {
        // Already inside the target's own frame: separation is just the radius.
        for (let i = 0; i < prediction.points.length; i++) {
          const d = prediction.points[i].length();
          if (d < targetMiss) {
            targetMiss = d;
            targetMissTime = prediction.times[i];
          }
        }
        targetPeriapsis = Math.min(targetPeriapsis, periapsisRadius(prediction.elements));
      }

      // Earliest sphere-of-influence entry among this body's children.
      const encounter = this.firstEncounter(prediction, body, arcEndTime);

      let terminator: RouteArc['terminator'] = 'horizon';
      let endTime = arcEndTime;
      let next: { body: CelestialBody; time: number } | null = null;

      if (arcImpact && (!encounter || arcImpact.time <= encounter.time)) {
        terminator = 'impact';
        endTime = arcImpact.time;
        impacts = true;
        impactBody = body;
      } else if (encounter && (!arcSoiExit || encounter.time <= arcSoiExit.time)) {
        terminator = 'soiEntry';
        endTime = encounter.time;
        next = { body: encounter.body, time: encounter.time };
        events.push({
          kind: 'soiEntry',
          time: encounter.time,
          position: stateVectorAt(prediction.elements, encounter.time).position,
          body: encounter.body,
          distance: encounter.body.sphereOfInfluence,
          label: `Entering ${encounter.body.name} sphere of influence`,
        });
        if (target && encounter.body.id === target.id) {
          arrived = true;
          arrivalTime = Math.min(arrivalTime, encounter.time);
        }
      } else if (arcSoiExit && body.parent) {
        terminator = 'soiExit';
        endTime = arcSoiExit.time;
        next = { body: body.parent, time: arcSoiExit.time };
        events.push(arcSoiExit);
      }

      const trimmed = trimTo(prediction, endTime);
      arcs.push({
        referenceBody: body,
        points: trimmed.points,
        times: trimmed.times,
        startTime: t,
        endTime,
        terminator,
        prediction,
      });
      for (const e of prediction.events) {
        if (e.kind !== 'soiExit' && e.time <= endTime + 1e-6) events.push(e);
      }

      // The first arc flown inside the target's own sphere of influence is the
      // one that decides whether the arrival is a capture or a crater. Whatever
      // the conic does after that is the arrival macro's problem, not the route
      // planner's, so stop chaining rather than paying for arcs nobody reads.
      if (target && body.id === target.id) break;

      if (!next) break;

      const sv = stateVectorAt(prediction.elements, next.time);
      const converted = this.system.frames.transformState(
        sv.position,
        sv.velocity,
        body.inertialFrameId,
        next.body.inertialFrameId,
        next.time,
      );
      body = next.body;
      pos = converted.position;
      vel = converted.velocity;
      t = next.time;
    }

    const last = arcs[arcs.length - 1];
    const endTime = last ? last.endTime : t0;
    const endState = last ? stateVectorAt(last.prediction.elements, endTime) : { position: pos, velocity: vel };

    events.sort((a, b) => a.time - b.time);

    return {
      arcs,
      events,
      impacts,
      impactBody,
      endBody: last ? last.referenceBody : referenceBody,
      endPosition: endState.position,
      endVelocity: endState.velocity,
      endTime,
      arrived,
      arrivalTime,
      targetMiss,
      targetMissTime,
      targetPeriapsis,
    };
  }

  /**
   * How far ahead one arc is worth propagating.
   *
   * Never past the point where the vessel crosses this body's sphere of
   * influence, because past that the conic is fiction — and, more practically,
   * because the sample count is fixed, so an over-long horizon buys nothing but
   * a coarser sampling interval. That coarseness is not cosmetic: encounter
   * detection walks these samples, and an escape arc given a multi-week horizon
   * spaces them hours apart, which is wide enough to step clean over a small
   * moon and report a route as clear when it is not.
   *
   * The crossing is solved in closed form: the true anomaly at which the conic
   * reaches the boundary radius, then the time to it.
   */
  private arcHorizon(elements: OrbitalElements, body: CelestialBody, t: number): number {
    const bound = !isHyperbolic(elements);
    const period = bound ? orbitalPeriod(elements) : Infinity;
    const soi = body.sphereOfInfluence;
    if (!Number.isFinite(soi)) return period;

    const e = elements.eccentricity;
    const p = elements.semiMajorAxis * (1 - e * e);
    const cosNu = e > 1e-12 ? (p / soi - 1) / e : 2;
    // Outside [-1, 1] the conic never reaches the boundary at all.
    if (!(cosNu >= -1 && cosNu <= 1)) return period;

    const exit = timeToTrueAnomaly(elements, Math.acos(cosNu), t);
    if (!Number.isFinite(exit)) return bound ? period : 6 * 3600;
    // Overshoot slightly so `predict` samples a point genuinely outside the
    // boundary and emits its `soiExit` rather than stopping one sample short.
    return Math.min(period, exit + Math.max(exit * 0.02, 1));
  }

  /**
   * Earliest time on `prediction` at which the vessel crosses inside a child
   * body's sphere of influence, or null.
   *
   * Two passes. The sample walk finds a bracket — either a sample already
   * inside the sphere, or a local minimum of the separation curve close enough
   * to be worth a second look — and the bracket is then refined by ternary
   * search and the crossing bisected out of it. Without the second pass a small
   * moon can sit entirely between two samples of a long heliocentric arc and be
   * missed, which is exactly the failure this whole mechanism exists to
   * prevent.
   */
  private firstEncounter(
    prediction: TrajectoryPrediction,
    body: CelestialBody,
    arcEndTime: number,
  ): { body: CelestialBody; time: number } | null {
    let best: { body: CelestialBody; time: number } | null = null;

    const times = prediction.times;
    let last = times.length - 1;
    while (last > 0 && times[last] > arcEndTime) last--;

    for (const child of body.children) {
      const soi = child.sphereOfInfluence;
      if (!Number.isFinite(soi) || soi <= 0) continue;

      // Separations at the samples `predict` already produced, one frame
      // transform each. Everything below refines within this table rather than
      // re-walking it, which is what keeps a solver iteration affordable.
      const separations: number[] = new Array(last + 1);
      for (let i = 0; i <= last; i++) {
        separations[i] = prediction.points[i].distanceTo(this.bodyPositionIn(child, body, times[i]));
      }

      const sep = (t: number): number =>
        stateVectorAt(prediction.elements, t).position.distanceTo(this.bodyPositionIn(child, body, t));

      let crossing: number | null = null;

      // A sample that already lands inside the sphere is unambiguous; take the
      // earliest one and bisect back for the moment of crossing.
      for (let i = 0; i <= last; i++) {
        if (separations[i] < soi) {
          crossing = i === 0 ? times[0] : bisectCrossing(sep, times[i - 1], times[i], soi);
          break;
        }
      }

      // Otherwise the encounter, if there is one, happened entirely between two
      // samples — routine for a small moon on a long arc, and the case this
      // whole mechanism exists to catch. Refine every bracketed minimum close
      // enough to be worth a second look, earliest first. The endpoint is
      // included as a bracket: a closest approach that falls on the last sample
      // is exactly the shape an arc truncated at an SOI boundary produces.
      for (let i = 1; i <= last && crossing === null; i++) {
        const isMinimum =
          separations[i] <= separations[i - 1] && (i === last || separations[i] <= separations[i + 1]);
        if (!isMinimum || separations[i] >= soi * 24) continue;
        const lo = times[i - 1];
        const hi = times[Math.min(last, i + 1)];
        const tMin = ternaryMinimum(sep, lo, hi);
        if (sep(tMin) < soi) crossing = bisectCrossing(sep, lo, tMin, soi);
      }

      if (crossing !== null && (!best || crossing < best.time)) best = { body: child, time: crossing };
    }

    return best;
  }

  /** Closest approach of a predicted arc to a body outside its reference frame. */
  private closestSeparation(
    prediction: TrajectoryPrediction,
    body: CelestialBody,
    other: CelestialBody,
  ): { distance: number; time: number } | null {
    let bestDistance = Infinity;
    let bestIndex = -1;
    for (let i = 0; i < prediction.points.length; i++) {
      const d = prediction.points[i].distanceTo(this.bodyPositionIn(other, body, prediction.times[i]));
      if (d < bestDistance) {
        bestDistance = d;
        bestIndex = i;
      }
    }
    if (bestIndex < 0) return null;

    // Refine against the sampling interval, so the reported miss is a property
    // of the trajectory rather than of how many samples it happened to get.
    const times = prediction.times;
    const lo = times[Math.max(0, bestIndex - 1)];
    const hi = times[Math.min(times.length - 1, bestIndex + 1)];
    if (hi > lo) {
      const sep = (t: number): number =>
        stateVectorAt(prediction.elements, t).position.distanceTo(this.bodyPositionIn(other, body, t));
      const tMin = ternaryMinimum(sep, lo, hi);
      const dMin = sep(tMin);
      if (dMin < bestDistance) return { distance: dMin, time: tMin };
    }
    return { distance: bestDistance, time: times[bestIndex] };
  }

  /** Centre of `other` expressed in `frame`'s inertial frame at time `t`. */
  private bodyPositionIn(other: CelestialBody, frame: CelestialBody, t: number): Vec3 {
    return this.system.frames.transformPosition(
      new Vec3(0, 0, 0),
      other.inertialFrameId,
      frame.inertialFrameId,
      t,
    );
  }
}

/** One conic of a patched-conic route. */
export interface RouteArc {
  referenceBody: CelestialBody;
  /** Sampled path in `referenceBody`'s inertial frame, trimmed to the arc. */
  points: Vec3[];
  /** Times matching `points`. */
  times: number[];
  startTime: number;
  endTime: number;
  /** Why this arc stopped, and therefore what (if anything) follows it. */
  terminator: 'soiExit' | 'soiEntry' | 'impact' | 'horizon';
  /** The untrimmed single-conic prediction this arc came from. */
  prediction: TrajectoryPrediction;
}

export interface ChainedPrediction {
  arcs: RouteArc[];
  events: TrajectoryEvent[];
  /** True when some arc terminates by hitting its own reference body. */
  impacts: boolean;
  /** The body that was hit, when `impacts`. */
  impactBody: CelestialBody | null;
  endBody: CelestialBody;
  /** Final state, in `endBody`'s inertial frame. */
  endPosition: Vec3;
  endVelocity: Vec3;
  endTime: number;
  /** True when the chain enters `arriveAtBodyId`'s sphere of influence. */
  arrived: boolean;
  arrivalTime: number;
  /** Closest the route comes to the arrival target, metres. */
  targetMiss: number;
  targetMissTime: number;
  /** Periapsis radius of the arc flown inside the target's own frame. */
  targetPeriapsis: number;
}

export interface ChainOptions extends PredictOptions {
  /** Hard cap on the number of conics. */
  maxArcs?: number;
  /** Look-ahead across the whole chain, seconds. */
  totalHorizon?: number;
  /** Body the route is trying to reach; drives `arrived` and `targetMiss`. */
  arriveAtBodyId?: string;
  /** Per-body terrain ceiling, so each arc's impact test uses its own world. */
  terrainCeilingFor?: (body: CelestialBody) => number;
}

/** Samples of `prediction` up to and including `endTime`, plus the exact end. */
function trimTo(prediction: TrajectoryPrediction, endTime: number): { points: Vec3[]; times: number[] } {
  const points: Vec3[] = [];
  const times: number[] = [];
  for (let i = 0; i < prediction.points.length; i++) {
    if (prediction.times[i] > endTime) break;
    points.push(prediction.points[i]);
    times.push(prediction.times[i]);
  }
  if (times.length === 0 || times[times.length - 1] < endTime - 1e-9) {
    points.push(stateVectorAt(prediction.elements, endTime).position);
    times.push(endTime);
  }
  return { points, times };
}

/**
 * First time in [lo, hi] at which `f` drops below `threshold`.
 *
 * Iteration counts here and in `ternaryMinimum` are deliberately modest: each
 * evaluation costs a Kepler solve and a frame transform, and the solver runs
 * these thousands of times. 24 bisections shrink the bracket by 1.7e7, which is
 * already far finer than the sampling interval that produced it.
 */
function bisectCrossing(f: (t: number) => number, lo: number, hi: number, threshold: number): number {
  let a = lo;
  let b = hi;
  for (let i = 0; i < 24; i++) {
    const m = (a + b) / 2;
    if (f(m) < threshold) b = m;
    else a = m;
  }
  return b;
}

/** Argument of the minimum of a unimodal `f` bracketed by [lo, hi]. */
function ternaryMinimum(f: (t: number) => number, lo: number, hi: number): number {
  let a = lo;
  let b = hi;
  for (let i = 0; i < 24; i++) {
    const m1 = a + (b - a) / 3;
    const m2 = b - (b - a) / 3;
    if (f(m1) < f(m2)) b = m2;
    else a = m1;
  }
  return (a + b) / 2;
}
